import { PROCESS_STATUS_KEY_SHIPPED, STOCK_ROW_STATE } from '../resource/stockConst';

/**
 * แปลงผลจาก API เป็น map ตาม itemId เพื่อให้ lookup ต่อแถวได้เร็ว
 * @param {Array} items - items จาก /claims/stock/availability
 */
export const toStockMap = (items) => {
  const map = {};
  (items ?? []).forEach((item) => {
    if (item?.itemId) {
      map[item.itemId] = item;
    }
  });
  return map;
};

/** เคลมที่จัดส่งเรียบร้อยแล้วไม่ต้องกันสต็อก และไม่ต้องล็อก */
export const isRowExcluded = (record) => record?.processStatus === PROCESS_STATUS_KEY_SHIPPED;

/**
 * นับว่าแต่ละ itemId ถูกเลือกไปแล้วกี่ชิ้นจากแถวที่ติ๊กอยู่
 * ใช้ทำ preview ให้เห็นทันทีว่าเลือกเกินสต็อกหรือยัง ก่อนกดส่ง
 */
export const countSelectedByItem = (dataSource, selectedRowKeys, rowKey = 'claimId') => {
  const selected = new Set(selectedRowKeys ?? []);
  const counter = {};
  (dataSource ?? []).forEach((record) => {
    if (!selected.has(record?.[rowKey]) || isRowExcluded(record)) {
      return;
    }
    const itemId = record?.itemCode;
    if (!itemId) {
      return;
    }
    counter[itemId] = (counter[itemId] ?? 0) + (record?.claimQty ?? 1);
  });
  return counter;
};

/**
 * สรุปสถานะสต็อกของ 1 แถว ใช้ทั้งกับสีแถว การ disable checkbox และข้อความอธิบาย
 * @returns {{state: string, available: number, selected: number, reason: string}}
 */
export const getRowStockState = ({ record, stockMap, selectedByItem }) => {
  if (isRowExcluded(record)) {
    return { state: STOCK_ROW_STATE.EXCLUDED, available: 0, selected: 0, reason: '' };
  }

  const itemId = record?.itemCode;
  const stock = itemId ? stockMap?.[itemId] : null;

  if (!itemId || !stock || !stock.found) {
    return {
      state: STOCK_ROW_STATE.UNKNOWN,
      available: 0,
      selected: 0,
      reason: 'ไม่พบสินค้านี้ในข้อมูลสต็อกล่าสุด',
    };
  }
  if (stock.stale) {
    return {
      state: STOCK_ROW_STATE.STALE,
      available: stock.effectiveAvailableQty ?? 0,
      selected: 0,
      reason: 'ข้อมูลสต็อกเก่าเกินกำหนด รอข้อมูลรอบใหม่จากระบบ CS',
    };
  }

  const available = stock.effectiveAvailableQty ?? 0;
  const need = record?.claimQty ?? 1;
  const selected = selectedByItem?.[itemId] ?? 0;

  if (available < need) {
    return {
      state: STOCK_ROW_STATE.OUT_OF_STOCK,
      available,
      selected,
      reason: `ของไม่พอ เหลือ ${available}`,
    };
  }
  if (selected > available) {
    return {
      state: STOCK_ROW_STATE.OVER_SELECTED,
      available,
      selected,
      reason: `เลือก ${selected} เกินของที่เหลือ ${available}`,
    };
  }
  return { state: STOCK_ROW_STATE.OK, available, selected, reason: '' };
};

/** แถวที่ติ๊กไม่ได้เลย (ของหมด / ไม่มีข้อมูล / ข้อมูลเก่า) */
export const isRowLocked = (state) =>
  state === STOCK_ROW_STATE.OUT_OF_STOCK ||
  state === STOCK_ROW_STATE.UNKNOWN ||
  state === STOCK_ROW_STATE.STALE;

/**
 * สรุปภาพรวมของที่เลือกอยู่ ใช้แสดงแถบเตือนด้านบนตาราง
 * จะได้ไม่ต้องไล่หาเองว่าเกินที่ item ไหน
 */
export const summarizeSelection = ({
  dataSource,
  selectedRowKeys,
  stockMap,
  rowKey = 'claimId',
}) => {
  const selectedByItem = countSelectedByItem(dataSource, selectedRowKeys, rowKey);
  const overItems = [];

  Object.keys(selectedByItem).forEach((itemId) => {
    const stock = stockMap?.[itemId];
    const available = stock?.found ? (stock?.effectiveAvailableQty ?? 0) : 0;
    if (selectedByItem[itemId] > available) {
      overItems.push({
        itemId,
        selected: selectedByItem[itemId],
        available,
        over: selectedByItem[itemId] - available,
      });
    }
  });

  const selected = new Set(selectedRowKeys ?? []);
  const overRowKeys = (dataSource ?? [])
    .filter((record) => {
      if (!selected.has(record?.[rowKey])) {
        return false;
      }
      const { state } = getRowStockState({ record, stockMap, selectedByItem });
      return state === STOCK_ROW_STATE.OVER_SELECTED || isRowLocked(state);
    })
    .map((record) => record?.[rowKey]);

  return { selectedByItem, overItems, overRowKeys, hasOver: overItems.length > 0 };
};

/** เอาเฉพาะแถวที่เกินออกจากรายการที่เลือก ผู้ใช้จะได้ไม่ต้องไล่ติ๊กออกเอง */
export const dropOverSelectedKeys = (selectedRowKeys, overRowKeys) => {
  const over = new Set(overRowKeys ?? []);
  return (selectedRowKeys ?? []).filter((key) => !over.has(key));
};

/** รวม itemCode ที่ไม่ซ้ำจากตาราง ไว้ยิงถามสต็อกครั้งเดียว */
export const collectItemCodes = (dataSource) => {
  const seen = new Set();
  (dataSource ?? []).forEach((record) => {
    if (record?.itemCode) {
      seen.add(record.itemCode);
    }
  });
  return Array.from(seen);
};
